import React from 'react'
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'
import { Router } from '../../../contexts/RouterContext'
import {
  formatRouteDate,
  formatFullAddress,
  formatLoadInfo,
  parseTrack,
  sortFreights,
} from './routePdfUtils'

const styles = StyleSheet.create({
  page: {
    padding: 14,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#222',
    lineHeight: 1.15,
  },
  title: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  headerGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
  },
  headerItem: {
    width: '50%',
    marginBottom: 2,
  },
  headerLabel: {
    fontWeight: 'bold',
  },
  freightBlock: {
    marginBottom: 4,
    paddingBottom: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: '#ddd',
  },
  freightTitle: {
    fontSize: 8.5,
    fontWeight: 'bold',
    marginBottom: 1,
  },
  line: {
    marginBottom: 1,
  },
  obsLine: {
    marginBottom: 1,
    fontStyle: 'italic',
  },
})

interface RoutePDFDocumentProps {
  router: Router
}

const RoutePDFDocument: React.FC<RoutePDFDocumentProps> = ({ router }) => {
  const { time, distance } = parseTrack(router.track)
  const sortedFreights = sortFreights(router.Freight ?? [])
  const vehicleLabel = router.Vehicle
    ? `${router.Vehicle.plate}${router.Vehicle.model ? ` (${router.Vehicle.model})` : ''}`
    : '-'

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>Roteiro #{router.id}</Text>

        <View style={styles.headerGrid}>
          <Text style={styles.headerItem}>
            <Text style={styles.headerLabel}>Veículo: </Text>
            {vehicleLabel}
          </Text>
          <Text style={styles.headerItem}>
            <Text style={styles.headerLabel}>Responsável: </Text>
            {router.responsible || '-'}
          </Text>
          <Text style={styles.headerItem}>
            <Text style={styles.headerLabel}>Data: </Text>
            {formatRouteDate(router.date)}
          </Text>
          <Text style={styles.headerItem}>
            <Text style={styles.headerLabel}>Volta à origem: </Text>
            {router.companyDestination ? 'Sim' : 'Não'}
          </Text>
          <Text style={styles.headerItem}>
            <Text style={styles.headerLabel}>Tempo: </Text>
            {time}
          </Text>
          <Text style={styles.headerItem}>
            <Text style={styles.headerLabel}>Quilometragem: </Text>
            {distance}
          </Text>
        </View>

        {sortedFreights.map((freight, index) => {
          const address = formatFullAddress(freight)
          const loadInfo = formatLoadInfo(freight.Load, freight.orderNumber)

          return (
            <View key={freight.id} style={styles.freightBlock} wrap={false}>
              <Text style={styles.freightTitle}>
                {index + 1}. {freight.type.toUpperCase()} —{' '}
                {freight.Company?.companyName || '-'}
              </Text>
              {address ? <Text style={styles.line}>{address}</Text> : null}
              <Text style={styles.line}>
                Liberado: {formatRouteDate(freight.readyToShip)} | Limite:{' '}
                {formatRouteDate(freight.deadLine)}
              </Text>
              <Text style={styles.line}>Carga: {loadInfo}</Text>
              {freight.observation ? (
                <Text style={styles.obsLine}>Obs: {freight.observation}</Text>
              ) : null}
            </View>
          )
        })}
      </Page>
    </Document>
  )
}

export default RoutePDFDocument
